import React, { useState } from 'react';
import { MapPin, ExternalLink, Copy, Check } from 'lucide-react';
import { getGoogleMapsLink, getOpenStreetMapLink } from '../../utils/geocodingService';

interface GPSVerificationLinkProps {
  latitude: number;
  longitude: number;
  className?: string;
}

export const GPSVerificationLink: React.FC<GPSVerificationLinkProps> = ({
  latitude,
  longitude,
  className = ''
}) => {
  const [copied, setCopied] = useState(false);
  const [showMapOptions, setShowMapOptions] = useState(false);

  const handleCopyCoordinates = async () => {
    try {
      await navigator.clipboard.writeText(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy coordinates:', error);
    }
  };

  const handleViewOnGoogleMaps = () => {
    window.open(getGoogleMapsLink(latitude, longitude), '_blank', 'noopener,noreferrer');
    setShowMapOptions(false);
  };

  const handleViewOnOpenStreetMap = () => {
    window.open(getOpenStreetMapLink(latitude, longitude), '_blank', 'noopener,noreferrer');
    setShowMapOptions(false);
  };

  return (
    <div className={`relative ${className}`}>
      <div className="flex items-center space-x-2">
        <div className="flex items-center space-x-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
          <MapPin className="h-4 w-4 text-blue-600" />
          <div className="text-sm">
            <span className="font-mono text-blue-900">
              {latitude.toFixed(4)}, {longitude.toFixed(4)}
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={handleCopyCoordinates}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
          title="Copy coordinates"
        >
          {copied ? (
            <Check className="h-4 w-4 text-green-600" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </button>

        <div className="relative">
          <button
            type="button"
            onClick={() => setShowMapOptions(!showMapOptions)}
            className="flex items-center space-x-1 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
          >
            <ExternalLink className="h-4 w-4" />
            <span>View on Map</span>
          </button>

          {showMapOptions && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowMapOptions(false)}
              />
              <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                <button
                  type="button"
                  onClick={handleViewOnGoogleMaps}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100"
                >
                  <p className="text-sm font-medium text-gray-900">Google Maps</p>
                  <p className="text-xs text-gray-500">View in Google Maps</p>
                </button>
                <button
                  type="button"
                  onClick={handleViewOnOpenStreetMap}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
                >
                  <p className="text-sm font-medium text-gray-900">OpenStreetMap</p>
                  <p className="text-xs text-gray-500">View in OpenStreetMap</p>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
